import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { SORT_OPTION_GROUPS, SORT_OPTIONS, type SortOption } from '@/lib/powers-data';
import { cn } from '@/lib/utils';

type PowerSortDropdownProps = {
  value: SortOption;
  onChange: (value: SortOption) => void;
  compact?: boolean;
  className?: string;
};

export default function PowerSortDropdown({
  value,
  onChange,
  compact = false,
  className,
}: PowerSortDropdownProps) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = SORT_OPTIONS.find((option) => option.id === value);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuWidth = menuRef.current?.offsetWidth ?? 240;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));

    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left,
      minWidth: Math.max(rect.width, 220),
      zIndex: 60,
      visibility: 'visible',
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [open, updatePosition, value]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      return;
    }

    const trigger = triggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - 240),
        minWidth: Math.max(rect.width, 220),
        zIndex: 60,
        visibility: 'hidden',
      });
    }

    setOpen(true);
  };

  const menu =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label="Ordenar exibição"
            style={menuStyle}
            className="max-h-[min(70vh,420px)] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl"
          >
            <div className="border-b border-gray-100 px-4 py-2.5">
              <p className="font-poppins text-xs font-semibold uppercase tracking-wide text-gray-400">
                Ordem de exibição
              </p>
            </div>
            <div className="py-1">
              {SORT_OPTION_GROUPS.map((group, groupIndex) => (
                <div key={group.label}>
                  {groupIndex > 0 && <div className="my-1 border-t border-gray-100" aria-hidden />}
                  <p className="px-4 pb-1 pt-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {group.label}
                  </p>
                  <ul>
                    {group.options.map((option) => {
                      const active = option.id === value;

                      return (
                        <li key={option.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={active}
                            onClick={() => {
                              onChange(option.id);
                              setOpen(false);
                            }}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left font-poppins text-sm transition-colors',
                              active
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            )}
                          >
                            <span>{option.label}</span>
                            {active && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Ordem de exibição: ${selected?.label ?? 'Mais recentes'}`}
        onClick={handleToggle}
        className={cn(
          'inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white font-poppins text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 whitespace-nowrap',
          compact ? 'px-3 py-1.5' : 'px-3.5 py-2',
          open && 'border-blue-300 ring-2 ring-blue-100',
          className
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{selected?.label ?? 'Mais recentes'}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
            open && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      {menu}
    </>
  );
}
